import { useState, type ReactNode } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { NavIcon } from "./NavIcon";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { Me, PageKey } from "@owners/shared-types";
import { CLIENT_LEVELS, LEVEL_LABELS, PAGE_LABELS } from "@owners/shared-types";
import { ASSET_MANAGEMENT_SECTIONS } from "@owners/access";
import { api, initials } from "../api";
import { PATHS } from "../routes";
import { FIN_REPORTS } from "../pages/FinancialReports";

/** Left menu in canvas order. HMA Users see Missing Data where others see Awaiting Approval. */
const TOP: PageKey[] = ["todays_update", "flash_reports", "awaiting_approval", "missing_data"];
const AM_SUBS: PageKey[] = ["reo_documents", "compliance", "debt_compliance", "budget_reforecast"];
const MIDDLE: PageKey[] = ["financial_reports", "my_reports", "receiver_reports", "receivership", "trends"];
const YOUR_SECTIONS: PageKey[] = ["team_members", "leadership", "guest_reviews", "request_records"];

/** The page a path belongs to (longest matching prefix), for the header title. */
function pageFor(pathname: string): PageKey | null {
  let best: PageKey | null = null, len = -1;
  for (const [k, p] of Object.entries(PATHS) as Array<[PageKey, string]>) {
    const hit = p === "/" ? pathname === "/" : pathname === p || pathname.startsWith(p + "/");
    if (hit && p.length > len) { best = k; len = p.length; }
  }
  return best;
}

/**
 * App frame, matching the Owners Module demo (canvas export): a navy header
 * across the top (logo mark, "Owners Module" + page title, search, Dataroom
 * or initials) over a navy left menu with icons, then the cream page.
 */
export function Shell({ me, children }: { me: Me; children: ReactNode }) {
  const has = (p: PageKey) => me.pages.includes(p);
  const loc = useLocation();
  const [amOpen, setAmOpen] = useState(true);
  const current = pageFor(loc.pathname);
  const amActive = current !== null && (current === "asset_management" || AM_SUBS.includes(current));
  // Menu badges (demo: "3 missing", "1 expired") — refreshed every few minutes, never blocks the menu.
  const counts = useQuery({ queryKey: ["nav-counts"], queryFn: () => api<{ counts: Partial<Record<PageKey, { n: number; label: string }>> }>("/api/nav-counts"),
    staleTime: 300_000, refetchInterval: 300_000, enabled: !!me.clientId || me.previewing });

  const link = (p: PageKey, sub = false) =>
    has(p) && (
      <NavLink key={p} to={PATHS[p]} end={p === "todays_update" || p === "asset_management"} className={sub ? "sub" : undefined}>
        {!sub && <NavIcon name={p} />}
        <span className="label">{PAGE_LABELS[p]}</span>
        {counts.data?.counts[p] && <span className="nav-badge">{counts.data.counts[p]!.label}</span>}
      </NavLink>
    );

  return (
    <div className="shell">
      <header className="appbar">
        <Link to="/" className="appbar-brand" aria-label="Today's Update">
          <span className="mark"><img src="/fyrehorse-logo.png" alt="" /></span>
          <span>
            <span className="kicker">Owners Module</span>
            <span className="title">{current ? PAGE_LABELS[current] : "Owners Module"}</span>
          </span>
        </Link>
        <QuickSearch me={me} />
        {/* DECISIONS: the Dataroom appears where the user's initials normally are, on every page. */}
        {has("dataroom") ? (
          <Link className="dataroom-btn" to={PATHS.dataroom}>Dataroom</Link>
        ) : (
          <Link className="avatar" to={PATHS.settings} title={`${me.name} · Settings`}>{initials(me.name)}</Link>
        )}
      </header>
      <nav className="nav" aria-label="Main">
        {TOP.map((p) => link(p))}
        {has("asset_management") && (
          <div className={`nav-group${amActive ? " has-active" : ""}`}>
            <div className="nav-head">
              <NavLink to={PATHS.asset_management} end>
                <NavIcon name="asset_management" />
                <span className="label">{PAGE_LABELS.asset_management}</span>
              </NavLink>
              <button type="button" className="caret" aria-label={amOpen ? "Collapse Asset Management" : "Expand Asset Management"} aria-expanded={amOpen}
                onClick={() => setAmOpen(!amOpen)}>
                <span style={{ display: "inline-block", transform: amOpen ? "none" : "rotate(-90deg)" }}><NavIcon name="chevron" size={14} /></span>
              </button>
            </div>
            {amOpen && AM_SUBS.map((p) => link(p, true))}
          </div>
        )}
        {!has("asset_management") && AM_SUBS.map((p) => link(p))}
        {MIDDLE.map((p) => p === "financial_reports" ? (
          <div key={p}>
            {link(p)}
            {/* Demo: statements as a sub-menu, shown while you're in Financial Reports. */}
            {has(p) && current === p && FIN_REPORTS.map(([k, l]) => {
              const on = (new URLSearchParams(loc.search).get("r") ?? "pnl") === k;
              return <Link key={k} to={`${PATHS.financial_reports}?r=${k}`} className={`sub${on ? " active" : ""}`}><span className="label">{l}</span></Link>;
            })}
          </div>
        ) : link(p))}
        {YOUR_SECTIONS.some(has) && <div className="group">Your sections</div>}
        {YOUR_SECTIONS.map((p) => link(p))}
        {link("settings")}
        {/* SPEC §0: Admins always get the Admin panel link and the Preview box below the menu. */}
        {me.level === "admin" && (
          <NavLink to={PATHS.admin_panel}><NavIcon name="admin_panel" /><span className="label">{PAGE_LABELS.admin_panel}</span></NavLink>
        )}
        <div className="spacer" />
        {me.level === "admin" && <PreviewBox me={me} />}
        <WhoAmI me={me} />
      </nav>
      <main className="main">
        {me.previewing && (
          <div className="dev-bar">
            PREVIEWING AS {LEVEL_LABELS[me.effectiveLevel].toUpperCase()} · {me.clientName} · READ-ONLY ·{" "}
            <button className="btn link" style={{ color: "inherit", textDecoration: "underline", padding: 0 }}
              onClick={async () => { await api("/api/me/preview", { method: "POST", body: { level: null } }); window.location.assign("/"); }}>
              STOP PREVIEWING
            </button>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}

/** Header search: jump to a page, or (for Dataroom users) to a hotel's Dataroom. */
function QuickSearch({ me }: { me: Me }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const hotels = useQuery({ queryKey: ["hotels"], queryFn: () => api<{ hotels: Array<{ id: string; name: string; code?: string | null }> }>("/api/hotels"), enabled: open && me.pages.includes("dataroom") });
  const term = q.trim().toLowerCase();
  const pages = term ? me.pages.filter((p) => PAGE_LABELS[p].toLowerCase().includes(term)).slice(0, 6) : [];
  const hotelHits = term && me.pages.includes("dataroom")
    ? (hotels.data?.hotels ?? []).filter((h) => h.name.toLowerCase().includes(term) || (h.code ?? "").toLowerCase().includes(term)).slice(0, 6) : [];
  const go = (to: string) => { setQ(""); setOpen(false); nav(to); };
  return (
    <div className="appbar-search" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false); }}>
      <NavIcon name="search" size={16} />
      <input value={q} placeholder={me.pages.includes("dataroom") ? "Search a hotel or page…" : "Search a page…"}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); (e.target as HTMLInputElement).blur(); }
          if (e.key === "Enter") { if (pages[0]) go(PATHS[pages[0]]); else if (hotelHits[0]) go(`${PATHS.dataroom}?hotel=${hotelHits[0].id}`); } }}
        aria-label="Search" />
      {open && term && (pages.length > 0 || hotelHits.length > 0) && (
        <div className="appbar-results" role="listbox">
          {pages.map((p) => <button key={p} type="button" onClick={() => go(PATHS[p])}><NavIcon name={p} size={14} /> {PAGE_LABELS[p]}</button>)}
          {hotelHits.length > 0 && <div className="sect">Hotels · open Dataroom</div>}
          {hotelHits.map((h) => <button key={h.id} type="button" onClick={() => go(`${PATHS.dataroom}?hotel=${h.id}`)}><NavIcon name="dataroom" size={14} /> {h.name}</button>)}
        </div>
      )}
    </div>
  );
}

/** "Preview logged in as" — Admin only, a box under Settings with a dropdown of levels. */
function PreviewBox({ me }: { me: Me }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const clients = useQuery({ queryKey: ["admin-clients"], queryFn: () => api<{ clients: { id: string; name: string }[] }>("/api/admin/clients") });
  const [clientId, setClientId] = useState<string>("");
  const [busy, setBusy] = useState(false);

  async function setPreview(level: string | null) {
    setBusy(true);
    try {
      const cid = clientId || me.clientId || clients.data?.clients[0]?.id;
      await api("/api/me/preview", { method: "POST", body: level ? { level, clientId: cid } : { level: null } });
      await qc.invalidateQueries();
      nav("/");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="preview">
      <div className="eyebrow" style={{ color: "#c8cfda" }}>Preview logged in as</div>
      {(clients.data?.clients.length ?? 0) > 1 && (
        <select value={clientId || me.clientId || ""} onChange={(e) => setClientId(e.target.value)} aria-label="Client to preview">
          {clients.data!.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      )}
      {/* SPEC §0: a dropdown of the six levels; the default is Asset Manager. */}
      <select value={me.previewing ? me.effectiveLevel : ""} disabled={busy || !clients.data}
        onChange={(e) => e.target.value && setPreview(e.target.value === "__stop" ? null : e.target.value)} aria-label="Level to preview">
        {!me.previewing && <option value="">Choose a level…</option>}
        {me.previewing && <option value="__stop">Admin · stop previewing</option>}
        {CLIENT_LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABELS[l]}</option>)}
      </select>
      <div>Admin only · see the module as this user level sees it</div>
    </div>
  );
}

function WhoAmI({ me }: { me: Me }) {
  const qc = useQueryClient();
  async function signOut() {
    await api("/api/auth/logout", { method: "POST" });
    qc.setQueryData(["me"], null);
    window.location.assign("/sign-in");
  }
  return (
    <div className="who">
      {me.name} · {LEVEL_LABELS[me.level]}
      <button onClick={signOut}>Sign out</button>
    </div>
  );
}

export { ASSET_MANAGEMENT_SECTIONS };
